/**
 * Verrouillage missile : aucun contact → détection → acquisition → verrou acquis (gamedesign §6).
 * Port de ASpaceShipPawn::FindMissileTarget / UpdateMissileLock.
 */
import type { Ship } from '../entities';
import type { SimState } from '../state';

/** Meilleure cible visible dans le cône, avec hystérésis. Ne modifie que la cible « précédente ». */
export function findMissileTarget(state: SimState): Ship | null {
  const lock = state.data.weapons.lock;
  const player = state.player;
  const w = state.weapons;
  const forward = player.forward();
  const origin = player.position.add(forward.scale(lock.originForward));
  const previous = w.previousCandidateId;
  let best: Ship | null = null;
  let bestScore = -Infinity;
  for (const candidate of state.ships) {
    if (!candidate.alive || candidate.team !== 'enemy') continue;
    const to = candidate.position.sub(origin);
    const distance = to.length();
    if (distance > lock.range) continue;
    const alignment = forward.dot(to.normalized());
    if (alignment < lock.minDot) continue;
    const active = candidate.id === w.lockTargetId && w.lockState !== 'none';
    const bonus = active ? lock.activeBonus : candidate.id === previous ? lock.previousBonus : 0;
    const score = alignment * 2 - distance / Math.max(lock.range, 1) + bonus;
    if (score > bestScore && state.collision.lineOfSight(origin, candidate.position)) {
      bestScore = score;
      best = candidate;
    }
  }
  w.previousCandidateId = best?.id ?? null;
  return best;
}

export function resetLock(state: SimState): void {
  const w = state.weapons;
  const changed = w.lockState !== 'none';
  w.lockTargetId = null;
  w.lockState = 'none';
  w.lockElapsed = 0;
  if (changed) state.emit({ type: 'lockChanged', time: state.time, state: 'none', targetId: null });
}

export function updateLock(state: SimState, dt: number): void {
  const lock = state.data.weapons.lock;
  const w = state.weapons;
  if (!state.player.alive) {
    resetLock(state);
    return;
  }
  const candidate = findMissileTarget(state);
  if (!candidate) {
    resetLock(state);
    return;
  }
  if (w.lockTargetId !== candidate.id) {
    w.lockTargetId = candidate.id;
    w.lockState = 'detected';
    w.lockElapsed = 0;
    state.emit({ type: 'lockChanged', time: state.time, state: 'detected', targetId: candidate.id });
  }
  w.lockElapsed += Math.max(0, dt);
  const acquisitionStart = Math.max(0, lock.detectionDuration);
  const lockTime = acquisitionStart + Math.max(0.05, lock.acquisitionDuration);
  const previous = w.lockState;
  w.lockState = w.lockElapsed < acquisitionStart ? 'detected' : w.lockElapsed < lockTime ? 'acquiring' : 'locked';
  if (previous !== w.lockState) {
    state.emit({ type: 'lockChanged', time: state.time, state: w.lockState, targetId: candidate.id });
  }
}

export function lockProgress(state: SimState): number {
  const lock = state.data.weapons.lock;
  const w = state.weapons;
  if (w.lockState === 'locked') return 1;
  if (w.lockState !== 'acquiring') return 0;
  return Math.max(0, Math.min(1, (w.lockElapsed - lock.detectionDuration) / Math.max(lock.acquisitionDuration, 0.01)));
}

/** Cible verrouillée encore valide, ou null. */
export function lockedTarget(state: SimState): Ship | null {
  const w = state.weapons;
  if (w.lockState !== 'locked') return null;
  const target = state.findShip(w.lockTargetId);
  return target?.alive ? target : null;
}
